"use client";

import type { SelectItem } from "@gettactic/components/src/form/UnifiedSelect/types";
import { HookFormUnifiedSelectInput } from "@gettactic/components/src/form/UnifiedSelectInput";
import { eligibleResourceTicketTypes } from "@gettactic/helpers/src/workplace-requests/resourceTicketTypes";
import { useOrgFeatureFlag, useResource } from "@gettactic/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  FieldPathByValue,
  FieldValues,
  PathValue,
  UseFormReturn
} from "react-hook-form";
import { useWorkplaceRequestTypes } from "@/features/workplace-requests/hooks/useWorkplaceRequestTypes";

/** The form field this component drives holds the selected ticket type ids. */
type TicketTypeIdsPath<T extends FieldValues> = FieldPathByValue<
  T,
  string[] | undefined
>;

interface ResourceTicketTypesFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: TicketTypeIdsPath<T>;
  /** Undefined while creating — nothing to hydrate from. */
  resourceId?: string;
  officeId: string;
  /** The live value of the form's `is_approvable` toggle. */
  isApprovable: boolean;
}

/**
 * Picks the request types that gate reservations on a resource.
 *
 * Only the `/v1/resources*` endpoints hydrate `ticket_type_ids`, and every admin
 * edit surface holds a resource from the legacy office listing instead — so the
 * stored value is fetched here rather than seeded from the form's `resource`
 * prop, and the field stays `undefined` (i.e. "leave the server's set alone")
 * until it arrives.
 */
export const ResourceTicketTypesField = <T extends FieldValues>({
  form,
  name,
  resourceId,
  officeId,
  isApprovable
}: ResourceTicketTypesFieldProps<T>) => {
  const { setValue } = form;

  // Gating a resource behind request types is meaningless without the feature —
  // there would be no way to file the request it redirects people to.
  const isWorkplaceRequestsEnabled = !!useOrgFeatureFlag("workplace_requests");

  // The `name` constraint is what makes this sound: react-hook-form still cannot
  // resolve a generic path's value type, so the cast stands in for the proof.
  const setTicketTypeIds = useCallback(
    (value: string[], shouldDirty: boolean) => {
      setValue(name, value as PathValue<T, TicketTypeIdsPath<T>>, {
        shouldDirty
      });
    },
    [name, setValue]
  );

  // The resource filter applies the resource-level location rules; while creating
  // there is no resource yet, so the office is the closest available scope.
  const { allElements: types, isFetching: isFetchingTypes } =
    useWorkplaceRequestTypes(
      resourceId ? { resource_id: resourceId } : { office_id: officeId },
      { enabled: isWorkplaceRequestsEnabled }
    );

  const { resource, error: resourceError } = useResource(resourceId, {
    enabled: !!resourceId && isWorkplaceRequestsEnabled
  });

  // The ref guards the one-time write; the state is what re-renders the select
  // out of its disabled state, which a ref flip alone would never do.
  const hasWrittenRef = useRef(false);
  const [isHydrated, setIsHydrated] = useState(!resourceId);
  useEffect(() => {
    if (hasWrittenRef.current || !resource || !isWorkplaceRequestsEnabled) {
      return;
    }
    hasWrittenRef.current = true;
    setTicketTypeIds(resource.ticket_type_ids ?? [], false);
    setIsHydrated(true);
  }, [resource, setTicketTypeIds, isWorkplaceRequestsEnabled]);

  // A resource that no longer needs approval cannot carry ticket types, so the
  // toggle going off has to clear them rather than leave a stale selection.
  const wasApprovable = useRef(isApprovable);
  useEffect(() => {
    if (wasApprovable.current && !isApprovable && isWorkplaceRequestsEnabled) {
      setTicketTypeIds([], true);
    }
    wasApprovable.current = isApprovable;
  }, [isApprovable, setTicketTypeIds, isWorkplaceRequestsEnabled]);

  // Same generic-path limitation as setTicketTypeIds above: the `name`
  // constraint is what makes the cast sound.
  const selectedIds = form.watch(name) as string[] | undefined;

  // A type stored on the resource stays listed even once it stops qualifying —
  // its settings can change long after it was picked here. The select resolves
  // its selection out of `items`, so leaving it out would strand it on the
  // resource: invisible in the trigger, yet still gating the booking. Listed,
  // it can be removed; it just can no longer be picked afresh.
  const items: SelectItem[] = useMemo(() => {
    const eligibleIds = new Set(
      eligibleResourceTicketTypes(types).map((type) => type.id)
    );
    const storedIds = new Set(selectedIds ?? []);
    return types
      .filter((type) => eligibleIds.has(type.id) || storedIds.has(type.id))
      .map((type) => ({
        id: type.id,
        name: type.name,
        description: eligibleIds.has(type.id)
          ? undefined
          : "Can no longer book this resource"
      }));
  }, [types, selectedIds]);

  // Only an unbookable row carries a description, so its presence is what tells
  // the helper text below to explain itself.
  const hasUnbookableSelection = items.some((item) => !!item.description);

  if (!isWorkplaceRequestsEnabled || !isApprovable) {
    return null;
  }

  // A failed hydration would silently overwrite the stored set on the next save,
  // so the field stays out of the way rather than guessing at an empty value.
  if (resourceError) {
    return (
      <p className="mt-4 text-muted-foreground text-sm">
        Could not load this resource&apos;s request types. Reload the page to
        configure them.
      </p>
    );
  }

  const isLoading = isFetchingTypes || !isHydrated;

  if (!isLoading && items.length === 0) {
    return (
      <p className="mt-4 text-muted-foreground text-sm">
        No request type can book this resource yet. A request type needs an
        enabled &quot;create reservation&quot; workflow, and must not itself
        require a reservation, before it can be used here.
      </p>
    );
  }

  return (
    <div className="ml-4">
      <HookFormUnifiedSelectInput
        form={form}
        name={name}
        label="Book through a request"
        multiselect
        searchable
        items={items}
        disabled={isLoading}
        placeholder="Select request types"
        searchPlaceholder="Search request types..."
        helperText={
          <>
            Once set, this resource can no longer be reserved directly — people
            book it by submitting one of these requests.
            {hasUnbookableSelection ? (
              <span className="block font-semibold underline">
                A selected request type can no longer book this resource: remove
                it or check its settings.
              </span>
            ) : null}
          </>
        }
      />
    </div>
  );
};
